import { Outlet, Navigate } from "react-router-dom";
import { AppSidebar } from "./AppSidebar";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useAuthStore } from "@/stores/authStore";
import { UnsavedChangesDialog } from "@/components/UnsavedChangesDialog";
import { useBeforeUnloadGuard } from "@/hooks/useUnsavedChanges";
import { usePingNotifications } from "@/hooks/ping/usePingNotifications";

/**
 * Shared layout that wraps all sidebar-enabled pages with a single SidebarProvider.
 * This ensures the sidebar open/collapsed state persists across page navigation —
 * the sidebar only toggles when the user clicks the toggle button.
 *
 * Also acts as an auth guard: if the user is not authenticated, they are
 * redirected back to the login page instead of seeing the sidebar.
 *
 * Hosts app-wide ping notifications (sound, toast, unread badge) so they fire
 * regardless of which page the provider is on.
 */
export const SidebarLayout = () => {
  const { isAuthenticated, isLoading } = useAuthStore();
  useBeforeUnloadGuard();

  // Ping notifications are mounted here rather than in ChatList so a provider
  // is alerted on every page, not only while the Pings screen is open.
  usePingNotifications();

  // If not authenticated and not still loading, redirect to login
  if (!isAuthenticated && !isLoading) {
    return <Navigate to="/" replace />;
  }

  return (
    <SidebarProvider defaultOpen={true}>
      <AppSidebar />
      <SidebarInset className="min-h-svh overflow-hidden">
        <div className="pointer-events-none absolute left-3 top-3 z-30 md:hidden">
          <SidebarTrigger className="pointer-events-auto h-10 w-10 rounded-full border border-border bg-background/90 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/80" />
        </div>
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </div>
      </SidebarInset>
      <UnsavedChangesDialog />
    </SidebarProvider>
  );
};
